import { useCallback, useEffect, useRef, useState } from "react";
import api, { API, formatApiErrorDetail } from "../api";
import { useAuth } from "../AuthContext";
import { useConfirm } from "../components/ConfirmDialog";

const STATUS_CHIP = {
  uploading: { bg: "var(--blue-soft)", fg: "var(--blue)", label: "Uploading" },
  queued: { bg: "var(--bg-input)", fg: "var(--text-dim)", label: "Queued" },
  processing: { bg: "var(--blue-soft)", fg: "var(--blue)", label: "Processing" },
  ready: { bg: "var(--green-soft)", fg: "var(--green)", label: "Ready" },
  failed: { bg: "var(--red-soft)", fg: "var(--red)", label: "Failed" },
};

const SUBSCORE_LABELS = {
  hook: "Hook", standalone: "Standalone", payoff: "Payoff",
  pace: "Pace", quotable: "Quotable", on_brand: "On-brand",
};

const FIELD = {
  width: "100%", padding: "8px 10px", borderRadius: 8,
  border: "1px solid var(--line)", fontSize: 14, boxSizing: "border-box",
  background: "var(--bg-input)", color: "var(--text)",
};

function fileUrl(jobId, clipId, kind, download) {
  return `${API}/clip-jobs/${jobId}/clips/${clipId}/file/${kind}${download ? "?download=1" : ""}`;
}

function formatBytes(n) {
  if (!n) return "0 MB";
  const mb = n / (1024 * 1024);
  return mb >= 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${mb.toFixed(0)} MB`;
}

function formatDuration(s) {
  const total = Math.round(s || 0);
  const m = Math.floor(total / 60), sec = total % 60;
  return `${m}:${String(sec).padStart(2, "0")}`;
}

function formatEta(etaSecs) {
  if (etaSecs == null) return null;
  if (etaSecs < 60) return "under a minute left";
  return `about ${Math.round(etaSecs / 60)} min left`;
}

function formatAgo(seconds) {
  if (seconds < 60) return `updated ${Math.max(0, Math.floor(seconds))}s ago`;
  return `updated ${Math.floor(seconds / 60)}m ago`;
}

// STALE_PROCESSING_SECS in backend/clip_jobs.py — a processing job this old
// with no update is swept back to queued the next time the worker claims.
const STALE_REQUEUE_SECS = 3 * 60 * 60;
const STALE_WARNING_SECS = 3 * 60;

export default function ClipsView({ goHome }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const confirm = useConfirm();

  const [jobs, setJobs] = useState([]);
  const [quota, setQuota] = useState(null);
  const [limitDraft, setLimitDraft] = useState("");
  const [health, setHealth] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", focus: "", max_clips: 8 });
  const [file, setFile] = useState(null);
  const [formErr, setFormErr] = useState("");
  const [upload, setUpload] = useState(null); // {sent, total}
  const [busy, setBusy] = useState("");
  const [expanded, setExpanded] = useState({}); // clip id -> {transcript, subscores}
  const [filterByJob, setFilterByJob] = useState({}); // job id -> all|approved|rejected
  const [now, setNow] = useState(() => Date.now());
  const fileInputRef = useRef(null);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const load = useCallback(() => api.get("/clip-jobs").then(({ data }) => {
    setJobs(Array.isArray(data) ? data : []);
  }).catch(() => {}), []);

  const loadQuota = useCallback(() => api.get("/clip-jobs/quota")
    .then(({ data }) => setQuota(data)).catch(() => {}), []);

  const loadHealth = useCallback(() => api.get("/clip-jobs/health")
    .then(({ data }) => setHealth(data)).catch(() => {}), []);

  useEffect(() => {
    load(); loadQuota(); loadHealth();
    const t = setInterval(() => { load(); loadQuota(); loadHealth(); }, 8000);
    return () => clearInterval(t);
  }, [load, loadQuota, loadHealth]);

  async function submit(e) {
    e?.preventDefault?.();
    if (!file) { setFormErr("Choose a video file."); return; }
    setFormErr("");
    try {
      const { data: job } = await api.post("/clip-jobs/upload/init", {
        filename: file.name, content_type: file.type || "application/octet-stream",
        total_size: file.size, title: form.title.trim() || null,
        focus: form.focus.trim() || null,
        max_clips: Number(form.max_clips) || 8,
      });
      const chunkSize = job.upload.chunk_size;
      setUpload({ sent: 0, total: file.size });
      let index = 0;
      for (let offset = 0; offset < file.size; offset += chunkSize) {
        const chunk = file.slice(offset, offset + chunkSize);
        await api.put(`/clip-jobs/${job.id}/upload/chunk`, chunk, {
          params: { index }, headers: { "Content-Type": "application/octet-stream" },
        });
        index += 1;
        setUpload({ sent: Math.min(offset + chunkSize, file.size), total: file.size });
      }
      await api.post(`/clip-jobs/${job.id}/upload/complete`);
      setUpload(null);
      setShowForm(false);
      setForm({ title: "", focus: "", max_clips: 8 });
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      await load(); await loadQuota();
    } catch (err) {
      setUpload(null);
      setFormErr(formatApiErrorDetail(err?.response?.data?.detail) || "Upload failed.");
    }
  }

  async function saveLimit() {
    const n = parseInt(limitDraft, 10);
    if (Number.isNaN(n) || n < 0) return;
    try {
      const { data } = await api.post("/clip-jobs/quota", { limit: n });
      setQuota(data); setLimitDraft("");
    } catch { /* meter refreshes on next poll */ }
  }

  async function rerun(job) {
    setBusy(job.id);
    try { await api.post(`/clip-jobs/${job.id}/rerun`); await load(); await loadQuota(); }
    catch { } finally { setBusy(""); }
  }

  async function deleteJob(job) {
    const ok = await confirm({
      title: "Delete this clip job?",
      message: `"${job.title}"\n\nThis removes the uploaded video and every rendered clip. This cannot be undone.`,
      confirmLabel: "Delete",
    });
    if (!ok) return;
    setBusy(job.id);
    try { await api.delete(`/clip-jobs/${job.id}`); await load(); }
    catch { } finally { setBusy(""); }
  }

  async function approve(job, clip, value) {
    setBusy(`${job.id}:${clip.id}`);
    try { await api.patch(`/clip-jobs/${job.id}/clips/${clip.id}`, { approved: value }); await load(); }
    catch { } finally { setBusy(""); }
  }

  const toggleExpanded = (clipId) => setExpanded((m) => ({ ...m, [clipId]: !m[clipId] }));

  return (
    <div className="view" data-testid="clips-view">
      <div className="crumb">
        <a onClick={goHome} data-testid="clips-back-to-home">← Home</a>
        {" / "}
        Clips
      </div>

      <div className="ver-head" style={{ marginTop: 16 }}>
        <div>
          <div className="sec-head" style={{ margin: 0 }}>Clips</div>
          <div className="sec-desc">
            Upload a long recording — a Zoom call, interview, or podcast — and get back
            scored, captioned promo clips to review, approve, and download. Rendering runs
            on Marco's Mac and can take a while for a long video.
          </div>
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
        <button className="btn btn-sm" onClick={() => setShowForm((v) => !v)} data-testid="clips-upload-toggle">
          {showForm ? "Cancel" : "Upload a video"}
        </button>

        {health && (() => {
          const down = !health.ok || health.stale;
          const label = health.stale ? "Worker not checked in" : health.ok ? "Worker online" : "Worker offline";
          return (
            <span title={health.stale
                    ? `Last check-in: ${health.at || "never"} — the Mac may be asleep or the worker stopped.`
                    : (health.detail || "")}
              style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 5,
                       color: down ? "var(--red)" : "var(--green)" }}>
              <span style={{ width: 7, height: 7, borderRadius: 999,
                             background: down ? "var(--red)" : "var(--green)" }} />
              {label}
              {down && !health.stale && health.detail ? ` — ${health.detail.slice(0, 80)}` : ""}
            </span>
          );
        })()}

        {quota && (
          <span style={{ fontSize: 12, color: quota.remaining === 0 ? "var(--amber)" : "var(--text-dim)",
                         display: "flex", alignItems: "center", gap: 6 }}>
            <strong style={{ fontWeight: 600 }}>{quota.used} of {quota.limit}</strong>
            videos processed this week
            {quota.remaining === 0 && " — queued videos start Monday"}
            {isAdmin && (
              <>
                <input value={limitDraft} onChange={(e) => setLimitDraft(e.target.value)}
                  placeholder={String(quota.limit)} inputMode="numeric" title="Weekly video limit"
                  style={{ width: 46, padding: "2px 6px", borderRadius: 6, fontSize: 12,
                           border: "1px solid var(--line)", background: "var(--bg-input)",
                           color: "var(--text)", marginLeft: 6 }} />
                <button onClick={saveLimit} disabled={!limitDraft.trim()} className="btn btn-sm">
                  Set limit
                </button>
              </>
            )}
          </span>
        )}
      </div>

      {showForm && (
        <form onSubmit={submit} style={{ marginBottom: 20, padding: 16, borderRadius: 10,
            border: "1px solid var(--line)", background: "var(--bg-panel)", display: "grid", gap: 10, maxWidth: 520 }}>
          <input ref={fileInputRef} type="file" accept="video/*"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            data-testid="clips-file-input" />
          <input style={FIELD} placeholder="Title (optional — defaults to the filename)"
            value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <textarea style={{ ...FIELD, minHeight: 60 }} placeholder={
            "Focus (optional) — e.g. \"only Ward's answers\" or \"anything about booking athletes\""}
            value={form.focus} onChange={(e) => setForm({ ...form, focus: e.target.value })} />
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--text-dim)" }}>
            Max clips
            <input type="number" min={1} max={20} style={{ ...FIELD, width: 70 }}
              value={form.max_clips} onChange={(e) => setForm({ ...form, max_clips: e.target.value })} />
          </label>
          {file && <div style={{ fontSize: 12, color: "var(--text-dim)" }}>{file.name} — {formatBytes(file.size)}</div>}
          {upload && (
            <div data-testid="clips-upload-progress">
              <div style={{ height: 6, borderRadius: 4, background: "var(--bg-input)", overflow: "hidden" }}>
                <div style={{ height: "100%", background: "var(--accent)",
                              width: `${Math.round((upload.sent / upload.total) * 100)}%` }} />
              </div>
              <div style={{ fontSize: 12, color: "var(--text-dim)", marginTop: 4 }}>
                Uploading… {formatBytes(upload.sent)} of {formatBytes(upload.total)}
              </div>
            </div>
          )}
          {formErr && <div style={{ color: "var(--red)", fontSize: 13 }}>{formErr}</div>}
          <button className="btn" disabled={!!upload} type="submit">
            {upload ? "Uploading…" : "Upload"}
          </button>
        </form>
      )}

      {jobs.length === 0 ? (
        <div className="disc-empty">No clip jobs yet — upload a video to get started.</div>
      ) : (
        <div style={{ display: "grid", gap: 14 }}>
          {jobs.map((job) => (
            <ClipJobCard key={job.id} job={job} busy={busy} isAdmin={isAdmin} now={now}
              filter={filterByJob[job.id] || "all"}
              setFilter={(f) => setFilterByJob((m) => ({ ...m, [job.id]: f }))}
              expanded={expanded} toggleExpanded={toggleExpanded}
              onRerun={() => rerun(job)} onDelete={() => deleteJob(job)}
              onApprove={(clip, value) => approve(job, clip, value)} />
          ))}
        </div>
      )}
    </div>
  );
}

function ClipJobCard({ job, busy, isAdmin, now, filter, setFilter, expanded, toggleExpanded,
                        onRerun, onDelete, onApprove }) {
  const chip = STATUS_CHIP[job.status] || STATUS_CHIP.queued;
  const pct = job.status === "uploading" && job.upload?.total_size
    ? Math.round((job.upload.received_bytes / job.upload.total_size) * 100) : null;
  const clips = (job.clips || []).slice().sort((a, b) => b.score - a.score);
  const visible = clips.filter((c) =>
    filter === "approved" ? c.approved === true
    : filter === "rejected" ? c.approved === false
    : true);
  const canDelete = job.status !== "processing" || isAdmin;

  const updatedMs = job.updated_at ? Date.parse(job.updated_at) : null;
  const secondsAgo = updatedMs != null && !Number.isNaN(updatedMs)
    ? Math.max(0, Math.floor((now - updatedMs) / 1000)) : null;
  const isProcessing = job.status === "processing";
  const isStale = isProcessing && secondsAgo != null && secondsAgo > STALE_WARNING_SECS;
  const processingPct = Math.max(0, Math.min(100, job.progress_pct ?? 0));
  const etaText = formatEta(job.eta_secs);

  return (
    <div data-testid={`clip-job-${job.id}`} style={{ padding: "14px 16px", borderRadius: 10,
        border: "1px solid var(--line)", background: "var(--bg-panel)", display: "grid", gap: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <strong style={{ fontSize: 14 }}>{job.title}</strong>
        <span style={{ background: chip.bg, color: chip.fg, borderRadius: 999,
                       padding: "2px 10px", fontSize: 12, fontWeight: 600 }}>
          {chip.label}{pct !== null ? ` ${pct}%` : ""}
        </span>
        {job.status === "queued" && (
          <span style={{ fontSize: 12, color: "var(--text-dim)" }}>Waiting for the Mac worker</span>
        )}
        {job.status === "failed" && (
          <button className="btn btn-sm" onClick={onRerun} disabled={busy === job.id || !job.source_file_id}
            title={job.source_file_id ? "" : "Source video is no longer available"}>
            Re-run
          </button>
        )}
        {canDelete && (
          <button onClick={onDelete} disabled={busy === job.id} title="Delete this clip job"
            aria-label="Delete this clip job" data-testid={`clip-job-delete-${job.id}`}
            style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer",
                     color: "var(--text-faint)", fontSize: 16, lineHeight: 1 }}>
            🗑
          </button>
        )}
      </div>

      {isProcessing && (
        isStale ? (
          <div data-testid={`clip-job-stale-${job.id}`} style={{ padding: "8px 10px", borderRadius: 8,
              background: "var(--amber-soft)", color: "var(--amber)", fontSize: 12 }}>
            No update for {Math.floor(secondsAgo / 60)} min — the Mac may be asleep or the worker
            stuck. It'll pick back up on its own, or requeue automatically after
            {` ${STALE_REQUEUE_SECS / 3600} `}hours of no progress.
          </div>
        ) : (
          <div data-testid={`clip-job-progress-${job.id}`} style={{ display: "grid", gap: 4 }}>
            <div style={{ height: 6, borderRadius: 4, background: "var(--bg-input)", overflow: "hidden" }}>
              <div style={{ height: "100%", background: "var(--accent)",
                            width: `${Math.round(processingPct)}%` }} />
            </div>
            <div style={{ fontSize: 11, color: "var(--text-dim)", display: "flex", gap: 8, flexWrap: "wrap" }}>
              <span>{Math.round(processingPct)}%</span>
              {etaText && <span>{etaText}</span>}
              {job.stage && <span>{job.stage}</span>}
              {secondsAgo != null && (
                <span style={{ color: "var(--text-faint)" }}>{formatAgo(secondsAgo)}</span>
              )}
            </div>
          </div>
        )
      )}

      {job.status === "failed" && job.error && (
        <div style={{ fontSize: 12, color: "var(--red)" }}>{job.error}</div>
      )}

      {job.status === "ready" && clips.length > 0 && (
        <>
          <div className="tabs" style={{ marginBottom: 0 }}>
            {[
              { key: "all", label: `All (${clips.length})` },
              { key: "approved", label: `Approved (${clips.filter((c) => c.approved === true).length})` },
              { key: "rejected", label: `Rejected (${clips.filter((c) => c.approved === false).length})` },
            ].map((t) => (
              <button key={t.key} className={`tab${filter === t.key ? " active" : ""}`}
                onClick={() => setFilter(t.key)}>
                {t.label}
              </button>
            ))}
          </div>
          <div style={{ display: "grid", gap: 14, gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))" }}>
            {visible.map((clip) => (
              <ClipCard key={clip.id} job={job} clip={clip} busy={busy}
                open={!!expanded[clip.id]} onToggle={() => toggleExpanded(clip.id)}
                onApprove={(v) => onApprove(clip, v)} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function ClipCard({ job, clip, busy, open, onToggle, onApprove }) {
  const isBusy = busy === `${job.id}:${clip.id}`;
  return (
    <div data-testid={`clip-card-${clip.id}`} style={{ borderRadius: 10, border: "1px solid var(--line)",
        background: "var(--bg-raised)", overflow: "hidden", display: "grid", gap: 8, padding: 10 }}>
      <video controls preload="none" poster={fileUrl(job.id, clip.id, "poster")}
        src={fileUrl(job.id, clip.id, "9x16")}
        style={{ width: "100%", aspectRatio: "9/16", borderRadius: 6, background: "#000" }} />
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span title="Overall score" style={{ fontFamily: "'IBM Plex Mono', monospace", fontWeight: 700,
            fontSize: 16, color: clip.score >= 70 ? "var(--green)" : clip.score >= 40 ? "var(--amber)" : "var(--red)" }}>
          {Math.round(clip.score)}
        </span>
        <strong style={{ fontSize: 13, flex: 1 }}>{clip.title}</strong>
      </div>
      <div style={{ fontSize: 11, color: "var(--text-faint)" }}>{formatDuration(clip.duration)}</div>
      {clip.why && <div style={{ fontSize: 12, color: "var(--text-dim)" }}>{clip.why}</div>}
      {(clip.flags || []).length > 0 && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {clip.flags.map((f) => (
            <span key={f} style={{ background: "var(--amber-soft)", color: "var(--amber)",
                borderRadius: 999, padding: "2px 8px", fontSize: 11 }}>{f}</span>
          ))}
        </div>
      )}
      <button onClick={onToggle} style={{ background: "none", border: "none", color: "var(--text-dim)",
          fontSize: 12, cursor: "pointer", textAlign: "left", padding: 0 }}>
        {open ? "▾ Hide details" : "▸ Subscores & transcript"}
      </button>
      {open && (
        <div style={{ display: "grid", gap: 6 }}>
          {Object.entries(SUBSCORE_LABELS).map(([k, label]) => (
            <div key={k} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11 }}>
              <span style={{ width: 70, color: "var(--text-faint)" }}>{label}</span>
              <div style={{ flex: 1, height: 5, borderRadius: 3, background: "var(--bg-input)" }}>
                <div style={{ height: "100%", borderRadius: 3, background: "var(--accent)",
                              width: `${((clip.subscores || {})[k] || 0) * 10}%` }} />
              </div>
              <span style={{ width: 18, textAlign: "right", color: "var(--text-faint)" }}>
                {(clip.subscores || {})[k] ?? "-"}
              </span>
            </div>
          ))}
          {clip.transcript && (
            <div style={{ fontSize: 11, color: "var(--text-faint)", maxHeight: 120, overflowY: "auto",
                         background: "var(--bg-input)", borderRadius: 6, padding: 8 }}>
              {clip.transcript}
            </div>
          )}
        </div>
      )}
      <div style={{ display: "flex", gap: 6 }}>
        <button className="btn btn-sm" disabled={isBusy} data-testid={`approve-${clip.id}`}
          onClick={() => onApprove(clip.approved === true ? null : true)}
          style={{ flex: 1, background: clip.approved === true ? "var(--green)" : undefined,
                   opacity: clip.approved === true ? 1 : 0.85 }}>
          {clip.approved === true ? "Approved ✓" : "Approve"}
        </button>
        <button className="btn btn-sm" disabled={isBusy} data-testid={`reject-${clip.id}`}
          onClick={() => onApprove(clip.approved === false ? null : false)}
          style={{ flex: 1, background: clip.approved === false ? "var(--red)" : undefined,
                   opacity: clip.approved === false ? 1 : 0.85 }}>
          {clip.approved === false ? "Rejected ✓" : "Reject"}
        </button>
      </div>
      <div style={{ display: "flex", gap: 6 }}>
        <a className="btn btn-sm" href={fileUrl(job.id, clip.id, "9x16", true)}
          style={{ flex: 1, textAlign: "center", textDecoration: "none" }}>9:16</a>
        <a className="btn btn-sm" href={fileUrl(job.id, clip.id, "16x9", true)}
          style={{ flex: 1, textAlign: "center", textDecoration: "none" }}>16:9</a>
      </div>
    </div>
  );
}
